import { useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { useRecaptcha } from "@/hooks/useRecaptcha";
import { Loader2, Send, Upload, X, Music, Image, FileText } from "lucide-react";
import ScrollReveal from "@/components/ScrollReveal";

// Validation schema for talent application
const applicationSchema = z.object({
  name: z.string().trim().min(1, "Name is required").max(100, "Name must be less than 100 characters"),
  email: z.string().trim().email("Invalid email address").max(255, "Email must be less than 255 characters"),
  phone: z.string().trim().max(30, "Phone must be less than 30 characters").optional().or(z.literal("")),
  demoReel: z.string().trim().url("Please enter a valid URL").max(500, "URL must be less than 500 characters").or(z.string().length(0)),
  experience: z.string().trim().min(1, "Please tell us about your experience").max(2000, "Experience must be less than 2000 characters"),
});

interface TalentApplicationFormProps {
  title?: string;
  subtitle?: string;
}

const TalentApplicationForm = ({ title, subtitle }: TalentApplicationFormProps) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { getToken } = useRecaptcha();

  // Defensive: strip any HTML tags from CMS-provided strings (and handle escaped entities)
  const stripHtml = (input: string) => {
    const firstPass = new DOMParser().parseFromString(input, "text/html").body.textContent || "";
    const secondPass = new DOMParser().parseFromString(firstPass, "text/html").body.textContent || "";
    return secondPass;
  };

  const safeTitle = title ? stripHtml(title) : "";
  const safeSubtitle = subtitle ? stripHtml(subtitle) : "";
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isUploadingHeadshot, setIsUploadingHeadshot] = useState(false);
  const [isUploadingResume, setIsUploadingResume] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [audioFile, setAudioFile] = useState<File | null>(null);
  const [uploadedAudioUrl, setUploadedAudioUrl] = useState<string | null>(null);
  const [headshotFile, setHeadshotFile] = useState<File | null>(null);
  const [uploadedHeadshotUrl, setUploadedHeadshotUrl] = useState<string | null>(null);
  const [resumeFile, setResumeFile] = useState<File | null>(null);
  const [uploadedResumeUrl, setUploadedResumeUrl] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const headshotInputRef = useRef<HTMLInputElement>(null);
  const resumeInputRef = useRef<HTMLInputElement>(null);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    demoReel: "",
    experience: "",
  });

  const ALLOWED_AUDIO_TYPES = [
    "audio/mpeg",
    "audio/mp3",
    "audio/wav",
    "audio/x-wav",
    "audio/mp4",
    "audio/m4a",
    "audio/x-m4a",
    "audio/ogg",
    "audio/webm",
  ];
  const ALLOWED_IMAGE_TYPES = [
    "image/jpeg",
    "image/jpg",
    "image/png",
    "image/webp",
  ];
  const ALLOWED_DOCUMENT_TYPES = [
    "application/pdf",
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  ];
  const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20MB
  const MAX_IMAGE_SIZE = 10 * 1024 * 1024; // 10MB for images

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!ALLOWED_AUDIO_TYPES.includes(file.type)) {
      setErrors((prev) => ({
        ...prev,
        audioFile: t("talentApplication.invalidFileType", "Please upload an audio file (MP3, WAV, M4A, OGG, or WebM)"),
      }));
      return;
    }

    // Validate file size
    if (file.size > MAX_FILE_SIZE) {
      setErrors((prev) => ({
        ...prev,
        audioFile: t("talentApplication.fileTooLarge", "File size must be less than 20MB"),
      }));
      return;
    }

    setErrors((prev) => ({ ...prev, audioFile: "" }));
    setAudioFile(file);
    setIsUploading(true);

    try {
      // Generate unique filename
      const timestamp = Date.now();
      const sanitizedName = file.name.replace(/[^a-zA-Z0-9.-]/g, "_");
      const filePath = `applications/${timestamp}-${sanitizedName}`;

      // Upload to Supabase storage
      const { data, error } = await supabase.storage
        .from("talent_applications")
        .upload(filePath, file, {
          cacheControl: "3600",
          upsert: false,
        });

      if (error) {
        console.error("Upload error:", error);
        setErrors((prev) => ({
          ...prev,
          audioFile: t("talentApplication.uploadFailed", "Failed to upload file. Please try again."),
        }));
        setAudioFile(null);
        return;
      }

      // Get public URL
      const { data: urlData } = supabase.storage.from("talent_applications").getPublicUrl(data.path);
      setUploadedAudioUrl(urlData.publicUrl);

      toast({
        title: t("talentApplication.uploadSuccess", "File Uploaded"),
        description: t("talentApplication.uploadSuccessDesc", "Your demo has been uploaded successfully."),
      });
    } catch (err) {
      console.error("Upload error:", err);
      setErrors((prev) => ({
        ...prev,
        audioFile: t("talentApplication.uploadFailed", "Failed to upload file. Please try again."),
      }));
      setAudioFile(null);
    } finally {
      setIsUploading(false);
    }
  };

  const handleRemoveFile = async () => {
    if (uploadedAudioUrl) {
      // Extract path from URL and delete from storage
      const urlParts = uploadedAudioUrl.split("/talent_applications/");
      if (urlParts[1]) {
        await supabase.storage.from("talent_applications").remove([urlParts[1]]);
      }
    }
    setAudioFile(null);
    setUploadedAudioUrl(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleHeadshotSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      setErrors((prev) => ({
        ...prev,
        headshotFile: t("talentApplication.invalidImageType", "Please upload an image file (JPG, PNG, or WebP)"),
      }));
      return;
    }

    // Validate file size
    if (file.size > MAX_IMAGE_SIZE) {
      setErrors((prev) => ({
        ...prev,
        headshotFile: t("talentApplication.imageTooLarge", "Image size must be less than 10MB"),
      }));
      return;
    }

    setErrors((prev) => ({ ...prev, headshotFile: "" }));
    setHeadshotFile(file);
    setIsUploadingHeadshot(true);

    try {
      const timestamp = Date.now();
      const sanitizedName = file.name.replace(/[^a-zA-Z0-9.-]/g, "_");
      const filePath = `applications/headshots/${timestamp}-${sanitizedName}`;

      const { data, error } = await supabase.storage
        .from("talent_applications")
        .upload(filePath, file, {
          cacheControl: "3600",
          upsert: false,
        });

      if (error) {
        console.error("Headshot upload error:", error);
        setErrors((prev) => ({
          ...prev,
          headshotFile: t("talentApplication.uploadFailed", "Failed to upload file. Please try again."),
        }));
        setHeadshotFile(null);
        return;
      }

      const { data: urlData } = supabase.storage.from("talent_applications").getPublicUrl(data.path);
      setUploadedHeadshotUrl(urlData.publicUrl);

      toast({
        title: t("talentApplication.headshotUploadSuccess", "Headshot Uploaded"),
        description: t("talentApplication.headshotUploadSuccessDesc", "Your headshot has been uploaded successfully."),
      });
    } catch (err) {
      console.error("Headshot upload error:", err);
      setErrors((prev) => ({
        ...prev,
        headshotFile: t("talentApplication.uploadFailed", "Failed to upload file. Please try again."),
      }));
      setHeadshotFile(null);
    } finally {
      setIsUploadingHeadshot(false);
    }
  };

  const handleRemoveHeadshot = async () => {
    if (uploadedHeadshotUrl) {
      const urlParts = uploadedHeadshotUrl.split("/talent_applications/");
      if (urlParts[1]) {
        await supabase.storage.from("talent_applications").remove([urlParts[1]]);
      }
    }
    setHeadshotFile(null);
    setUploadedHeadshotUrl(null);
    if (headshotInputRef.current) {
      headshotInputRef.current.value = "";
    }
  };

  const handleResumeSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!ALLOWED_DOCUMENT_TYPES.includes(file.type)) {
      setErrors((prev) => ({
        ...prev,
        resumeFile: t("talentApplication.invalidDocumentType", "Please upload a PDF or Word document"),
      }));
      return;
    }

    // Validate file size
    if (file.size > MAX_FILE_SIZE) {
      setErrors((prev) => ({
        ...prev,
        resumeFile: t("talentApplication.documentTooLarge", "Document size must be less than 20MB"),
      }));
      return;
    }

    setErrors((prev) => ({ ...prev, resumeFile: "" }));
    setResumeFile(file);
    setIsUploadingResume(true);

    try {
      const timestamp = Date.now();
      const sanitizedName = file.name.replace(/[^a-zA-Z0-9.-]/g, "_");
      const filePath = `applications/resumes/${timestamp}-${sanitizedName}`;

      const { data, error } = await supabase.storage
        .from("talent_applications")
        .upload(filePath, file, {
          cacheControl: "3600",
          upsert: false,
        });

      if (error) {
        console.error("Resume upload error:", error);
        setErrors((prev) => ({
          ...prev,
          resumeFile: t("talentApplication.uploadFailed", "Failed to upload file. Please try again."),
        }));
        setResumeFile(null);
        return;
      }

      const { data: urlData } = supabase.storage.from("talent_applications").getPublicUrl(data.path);
      setUploadedResumeUrl(urlData.publicUrl);

      toast({
        title: t("talentApplication.resumeUploadSuccess", "Resume Uploaded"),
        description: t("talentApplication.resumeUploadSuccessDesc", "Your resume has been uploaded successfully."),
      });
    } catch (err) {
      console.error("Resume upload error:", err);
      setErrors((prev) => ({
        ...prev,
        resumeFile: t("talentApplication.uploadFailed", "Failed to upload file. Please try again."),
      }));
      setResumeFile(null);
    } finally {
      setIsUploadingResume(false);
    }
  };

  const handleRemoveResume = async () => {
    if (uploadedResumeUrl) {
      const urlParts = uploadedResumeUrl.split("/talent_applications/");
      if (urlParts[1]) {
        await supabase.storage.from("talent_applications").remove([urlParts[1]]);
      }
    }
    setResumeFile(null);
    setUploadedResumeUrl(null);
    if (resumeInputRef.current) {
      resumeInputRef.current.value = "";
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});
    setIsSubmitting(true);

    try {
      // Validate form data
      const validatedData = applicationSchema.parse(formData);

      // Verify reCAPTCHA
      const recaptchaToken = await getToken("talent_application");
      if (recaptchaToken) {
        const { data: verifyData, error: verifyError } = await supabase.functions.invoke("verify-recaptcha", {
          body: { token: recaptchaToken, action: "talent_application" },
        });
        
        if (verifyError || !verifyData?.success) {
          console.error("reCAPTCHA verification failed:", verifyError || verifyData?.error);
          toast({
            title: t("talentApplication.error", "Error"),
            description: t("common.captchaFailed", "Security verification failed. Please try again."),
            variant: "destructive",
          });
          setIsSubmitting(false);
          return;
        }
      }

      // Build message with demo reel and experience
      const message = `[TALENT APPLICATION]

Demo Reel Link: ${validatedData.demoReel || "Not provided"}
Uploaded Audio Demo: ${uploadedAudioUrl || "Not provided"}
Headshot: ${uploadedHeadshotUrl || "Not provided"}
Resume: ${uploadedResumeUrl || "Not provided"}

Experience & Background:
${validatedData.experience}`;

      // Insert into database
      const { error } = await supabase
        .from("talent_requests")
        .insert({
          name: validatedData.name,
          email: validatedData.email,
          company: null,
          phone: validatedData.phone || null,
          message: message,
          talent_id: null,
          status: "new",
          request_type: "application",
        });

      if (error) {
        console.error("Form submission error:", error);
        toast({
          title: t("talentApplication.error", "Error"),
          description: t("talentApplication.errorDesc", "Failed to submit application. Please try again."),
          variant: "destructive",
        });
        return;
      }

      // Send email notification
      try {
        await supabase.functions.invoke("send-contact-notification", {
          body: {
            name: validatedData.name,
            email: validatedData.email,
            message: message,
            isTalentApplication: true,
          },
        });
      } catch (emailError) {
        console.error("Email notification error:", emailError);
      }

      toast({
        title: t("talentApplication.success", "Application Submitted"),
        description: t("talentApplication.successDesc", "Thank you for your interest! We'll be in touch soon."),
      });

      setFormData({ name: "", email: "", phone: "", demoReel: "", experience: "" });
      setAudioFile(null);
      setUploadedAudioUrl(null);
      setHeadshotFile(null);
      setUploadedHeadshotUrl(null);
      setResumeFile(null);
      setUploadedResumeUrl(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      if (headshotInputRef.current) {
        headshotInputRef.current.value = "";
      }
      if (resumeInputRef.current) {
        resumeInputRef.current.value = "";
      }
    } catch (err) {
      if (err instanceof z.ZodError) {
        const fieldErrors: Record<string, string> = {};
        err.errors.forEach((error) => {
          if (error.path[0]) {
            fieldErrors[error.path[0] as string] = error.message;
          }
        });
        setErrors(fieldErrors);
      } else {
        toast({
          title: t("talentApplication.error", "Error"),
          description: t("talentApplication.errorDesc", "Failed to submit application. Please try again."),
          variant: "destructive",
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="section-padding bg-muted/30">
      <div className="container-wide">
        <div className="max-w-2xl mx-auto">
          <ScrollReveal>
            <h2 className="font-display text-2xl sm:text-3xl md:text-4xl font-medium text-center mb-4 text-primary">
              {safeTitle || t("talentApplication.title", "Apply to Join Our Roster")}
            </h2>
            <p className="text-muted-foreground text-center mb-8">
              {safeSubtitle ||
                t(
                  "talentApplication.subtitle",
                  "Tell us about yourself and your experience. We review every application."
                )}
            </p>
          </ScrollReveal>

          <ScrollReveal delay={0.1}>
            <form onSubmit={handleSubmit} className="space-y-6 bg-background rounded-lg border border-border p-6 md:p-8">
              <div className="grid sm:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="app-name">
                    {t("talentApplication.name", "Full Name")} *
                  </Label>
                  <Input
                    id="app-name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    maxLength={100}
                    placeholder={t("talentApplication.namePlaceholder", "Your full name")}
                    className={`bg-background ${errors.name ? "border-destructive" : ""}`}
                    aria-invalid={!!errors.name}
                    aria-describedby={errors.name ? "app-name-error" : undefined}
                  />
                  {errors.name && (
                    <p id="app-name-error" className="text-sm text-destructive">{errors.name}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="app-email">
                    {t("talentApplication.email", "Email")} *
                  </Label>
                  <Input
                    id="app-email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    maxLength={255}
                    placeholder={t("talentApplication.emailPlaceholder", "your@email.com")}
                    className={`bg-background ${errors.email ? "border-destructive" : ""}`}
                    aria-invalid={!!errors.email}
                    aria-describedby={errors.email ? "app-email-error" : undefined}
                  />
                  {errors.email && (
                    <p id="app-email-error" className="text-sm text-destructive">{errors.email}</p>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="app-phone">
                  {t("talentApplication.phone", "Phone Number")}
                </Label>
                <Input
                  id="app-phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  maxLength={30}
                  placeholder={t("talentApplication.phonePlaceholder", "(555) 123-4567")}
                  className={`bg-background ${errors.phone ? "border-destructive" : ""}`}
                  aria-invalid={!!errors.phone}
                  aria-describedby={errors.phone ? "app-phone-error" : undefined}
                />
                {errors.phone && (
                  <p id="app-phone-error" className="text-sm text-destructive">{errors.phone}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="app-demoReel">
                  {t("talentApplication.demoReel", "Demo Reel Link")}
                </Label>
                <Input
                  id="app-demoReel"
                  name="demoReel"
                  type="url"
                  value={formData.demoReel}
                  onChange={handleChange}
                  maxLength={500}
                  placeholder={t("talentApplication.demoReelPlaceholder", "https://youtube.com/... or https://soundcloud.com/...")}
                  className={`bg-background ${errors.demoReel ? "border-destructive" : ""}`}
                  aria-invalid={!!errors.demoReel}
                  aria-describedby={errors.demoReel ? "app-demoReel-error" : undefined}
                />
                {errors.demoReel && (
                  <p id="app-demoReel-error" className="text-sm text-destructive">{errors.demoReel}</p>
                )}
                <p className="text-xs text-muted-foreground">
                  {t("talentApplication.demoReelHint", "Link to your voice demo, video reel, or portfolio (YouTube, Vimeo, SoundCloud, personal website). Or upload an audio file below.")}
                </p>
              </div>

              {/* Audio File Upload */}
              <div className="space-y-2">
                <Label htmlFor="app-audioFile">
                  {t("talentApplication.uploadDemo", "Upload Audio Demo")}
                </Label>
                
                {!audioFile ? (
                  <div 
                    className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors hover:border-primary/50 hover:bg-muted/50 ${errors.audioFile ? "border-destructive" : "border-border"}`}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <input
                      ref={fileInputRef}
                      id="app-audioFile"
                      type="file"
                      accept="audio/*"
                      onChange={handleFileSelect}
                      className="hidden"
                      aria-describedby={errors.audioFile ? "app-audioFile-error" : "app-audioFile-hint"}
                    />
                    <Upload className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
                    <p className="text-sm text-muted-foreground">
                      {t("talentApplication.dropOrClick", "Click to upload your audio demo")}
                    </p>
                    <p className="text-xs text-muted-foreground/70 mt-1">
                      {t("talentApplication.audioFormats", "MP3, WAV, M4A, OGG, WebM (max 20MB)")}
                    </p>
                  </div>
                ) : (
                  <div className="flex items-center gap-3 p-4 bg-muted/50 rounded-lg border border-border">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Music className="w-5 h-5 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{audioFile.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {(audioFile.size / (1024 * 1024)).toFixed(2)} MB
                        {isUploading && " • " + t("talentApplication.uploading", "Uploading...")}
                        {uploadedAudioUrl && " • " + t("talentApplication.uploaded", "Uploaded")}
                      </p>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={handleRemoveFile}
                      disabled={isUploading}
                      className="flex-shrink-0"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                )}
                
                {isUploading && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {t("talentApplication.uploading", "Uploading...")}
                  </div>
                )}
                
                {errors.audioFile && (
                  <p id="app-audioFile-error" className="text-sm text-destructive">{errors.audioFile}</p>
                )}
                
                <p id="app-audioFile-hint" className="text-xs text-muted-foreground">
                  {t("talentApplication.uploadHint", "Upload a voice demo or sample of your work")}
                </p>
              </div>

              {/* Headshot Upload */}
              <div className="space-y-2">
                <Label htmlFor="app-headshotFile">
                  {t("talentApplication.uploadHeadshot", "Upload Headshot")}
                </Label>
                
                {!headshotFile ? (
                  <div 
                    className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors hover:border-primary/50 hover:bg-muted/50 ${errors.headshotFile ? "border-destructive" : "border-border"}`}
                    onClick={() => headshotInputRef.current?.click()}
                  >
                    <input
                      ref={headshotInputRef}
                      id="app-headshotFile"
                      type="file"
                      accept="image/jpeg,image/jpg,image/png,image/webp"
                      onChange={handleHeadshotSelect}
                      className="hidden"
                      aria-describedby={errors.headshotFile ? "app-headshotFile-error" : "app-headshotFile-hint"}
                    />
                    <Image className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
                    <p className="text-sm text-muted-foreground">
                      {t("talentApplication.clickToUploadHeadshot", "Click to upload your headshot")}
                    </p>
                    <p className="text-xs text-muted-foreground/70 mt-1">
                      {t("talentApplication.imageFormats", "JPG, PNG, or WebP (max 10MB)")}
                    </p>
                  </div>
                ) : (
                  <div className="flex items-center gap-3 p-4 bg-muted/50 rounded-lg border border-border">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0 overflow-hidden">
                      {uploadedHeadshotUrl ? (
                        <img src={uploadedHeadshotUrl} alt="Headshot preview" className="w-full h-full object-cover" />
                      ) : (
                        <Image className="w-5 h-5 text-primary" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{headshotFile.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {(headshotFile.size / (1024 * 1024)).toFixed(2)} MB
                        {isUploadingHeadshot && " • " + t("talentApplication.uploading", "Uploading...")}
                        {uploadedHeadshotUrl && " • " + t("talentApplication.uploaded", "Uploaded")}
                      </p>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={handleRemoveHeadshot}
                      disabled={isUploadingHeadshot}
                      className="flex-shrink-0"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                )}
                
                {isUploadingHeadshot && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {t("talentApplication.uploading", "Uploading...")}
                  </div>
                )}
                
                {errors.headshotFile && (
                  <p id="app-headshotFile-error" className="text-sm text-destructive">{errors.headshotFile}</p>
                )}
                
                <p id="app-headshotFile-hint" className="text-xs text-muted-foreground">
                  {t("talentApplication.headshotHint", "Upload a professional headshot photo")}
                </p>
              </div>

              {/* Resume Upload */}
              <div className="space-y-2">
                <Label htmlFor="app-resumeFile">
                  {t("talentApplication.uploadResume", "Upload Resume")}
                </Label>
                
                {!resumeFile ? (
                  <div 
                    className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors hover:border-primary/50 hover:bg-muted/50 ${errors.resumeFile ? "border-destructive" : "border-border"}`}
                    onClick={() => resumeInputRef.current?.click()}
                  >
                    <input
                      ref={resumeInputRef}
                      id="app-resumeFile"
                      type="file"
                      accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                      onChange={handleResumeSelect}
                      className="hidden"
                      aria-describedby={errors.resumeFile ? "app-resumeFile-error" : "app-resumeFile-hint"}
                    />
                    <FileText className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
                    <p className="text-sm text-muted-foreground">
                      {t("talentApplication.clickToUploadResume", "Click to upload your resume")}
                    </p>
                    <p className="text-xs text-muted-foreground/70 mt-1">
                      {t("talentApplication.documentFormats", "PDF or Word document (max 20MB)")}
                    </p>
                  </div>
                ) : (
                  <div className="flex items-center gap-3 p-4 bg-muted/50 rounded-lg border border-border">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <FileText className="w-5 h-5 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{resumeFile.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {(resumeFile.size / (1024 * 1024)).toFixed(2)} MB
                        {isUploadingResume && " • " + t("talentApplication.uploading", "Uploading...")}
                        {uploadedResumeUrl && " • " + t("talentApplication.uploaded", "Uploaded")}
                      </p>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={handleRemoveResume}
                      disabled={isUploadingResume}
                      className="flex-shrink-0"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                )}
                
                {isUploadingResume && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {t("talentApplication.uploading", "Uploading...")}
                  </div>
                )}
                
                {errors.resumeFile && (
                  <p id="app-resumeFile-error" className="text-sm text-destructive">{errors.resumeFile}</p>
                )}
                
                <p id="app-resumeFile-hint" className="text-xs text-muted-foreground">
                  {t("talentApplication.resumeHint", "Upload your resume or CV")}
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="app-experience">
                  {t("talentApplication.experience", "Experience & Background")} *
                </Label>
                <Textarea
                  id="app-experience"
                  name="experience"
                  value={formData.experience}
                  onChange={handleChange}
                  required
                  maxLength={2000}
                  placeholder={t("talentApplication.experiencePlaceholder", "Tell us about your voice work, acting experience, training, languages you speak, and what type of work you're interested in...")}
                  rows={6}
                  className={`bg-background resize-none ${errors.experience ? "border-destructive" : ""}`}
                  aria-invalid={!!errors.experience}
                  aria-describedby={errors.experience ? "app-experience-error" : undefined}
                />
                {errors.experience && (
                  <p id="app-experience-error" className="text-sm text-destructive">{errors.experience}</p>
                )}
              </div>

              <Button
                type="submit"
                disabled={isSubmitting || isUploading || isUploadingHeadshot || isUploadingResume}
                size="lg"
                className="w-full"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t("talentApplication.submitting", "Submitting...")}
                  </>
                ) : (
                  <>
                    <Send className="mr-2 h-4 w-4" />
                    {t("talentApplication.submit", "Submit Application")}
                  </>
                )}
              </Button>
              <p className="text-[11px] text-muted-foreground/70 text-center mt-6 leading-relaxed">
                {t("common.recaptchaNotice", "This site is protected by reCAPTCHA and the Google")}{" "}
                <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-primary transition-colors">
                  {t("common.privacyPolicy", "Privacy Policy")}
                </a>{" "}
                {t("common.and", "and")}{" "}
                <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-primary transition-colors">
                  {t("common.termsOfService", "Terms of Service")}
                </a>{" "}
                {t("common.apply", "apply")}.
              </p>
            </form>
          </ScrollReveal>
        </div>
      </div>
    </section>
  );
};

export default TalentApplicationForm;
